import type { ComponentChildren, RefObject } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { peopleIn, type Column, type Grid as GridDoc, type Group, type Mark, type Meta, type Row, type Term } from '../data';
import { fmtLong, LEADER, leaderShort, ordinal, outcomeWords } from '../format';
import { cls, outcomeDay, type SortBy } from '../grid/Grid';
import { GLYPH, KEY_DEF, KEY_ORDER, KEY_SHORT } from '../grid/marks';
import { leaveSheet } from '../router';
import { NON_STATES, SocialLinks, TAGLINE } from './Chrome';
import { ShareButton, SharePanel, siteShare } from './Share';
import { foundAs, useElsewhere } from '../search';

// THE PHONE'S SHEETS (Design's phone handoff, design_handoff_phone_view/README.md, "Sheets"): Find, a
// full-screen layer; and the bottom sheets Who represents me?, Term items and key, Jump to a month and
// the Menu. Each is a history entry of its own (router.ts), so the phone's Back closes it; the page
// under it is inert while it is open, and Escape closes it. Phones only: a desktop keeps its controls.

export type Filter = 'all' | Group;
/** The item filter's four choices: the long words where there is room, the short ones on the phone's
 *  four segments, which share a 343px row. */
export const FILTERS: [Filter, string, string][] = [
  ['all', 'All items', 'All'], ['bills', 'Bills & resolutions', 'Bills'],
  ['budget', 'Budgets & spending', 'Budget'], ['noms', 'Nominations', 'Nominations'],
];

/** A layer over the page: the rest of the page inert (no Tab, no screen reader, no tap) while it is
 *  open, Escape closes it, and the focus returns to what opened it. The layer must be a child of the
 *  app's root, beside what it makes inert. */
export function useLayer(ref: RefObject<HTMLElement>, onClose: () => void, first?: string) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const me = ref.current;
    if (!me) return;
    const active = document.activeElement as HTMLElement | null;
    const opener = active?.classList.contains('sr-kbd-prime') ? primedFrom : active;
    const others = Array.from(me.parentElement?.children ?? []).filter((el) => el !== me) as HTMLElement[];
    for (const el of others) el.inert = true;
    const target = (first && me.querySelector<HTMLElement>(first)) || me.querySelector<HTMLElement>('[tabindex="-1"]');
    target?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close.current(); } };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      for (const el of others) el.inert = false;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);
}

/** A bottom sheet: the scrim (a tap on it closes), the title, and Close or Done. */
export function Sheet({ title, action = 'Close', onClose, children }: {
  title: string; action?: string; onClose: () => void; children: ComponentChildren;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayer(ref, onClose);
  const id = 'sr-sheet-' + title.replace(/\W+/g, '-').toLowerCase();
  return (
    <div ref={ref} class="sr-layer">
      <div class="sr-scrim" onClick={onClose} />
      <div class="sr-sheet-b" role="dialog" aria-modal="true" aria-labelledby={id} tabIndex={-1}>
        <div class="sr-sheet-b-head">
          <h2 class="sr-sheet-b-title" id={id}>{title}</h2>
          <button class="sr-sheet-b-close" onClick={onClose}>{action}</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** iPhone Safari opens the keyboard only for a focus made during the tap itself, and Find's box does
 *  not exist yet then; so the tap focuses a stand-in, and the box takes the focus from it. What had the
 *  focus before is kept, for the focus to return to when Find closes. */
let primedFrom: HTMLElement | null = null;
export function primeKeyboard() {
  primedFrom = document.activeElement as HTMLElement | null;
  const el = document.createElement('input');
  el.className = 'sr-kbd-prime';
  el.setAttribute('aria-hidden', 'true');
  el.tabIndex = -1;
  document.body.appendChild(el);
  el.focus({ preventScroll: true });
  setTimeout(() => el.remove(), 1000);
}

// ---- Find ------------------------------------------------------------------------------------

/** An item number as typed or printed, without dots, spaces or case: "H.R. 5334" and "hr5334" meet.
 *  The hyphen stays, so PN12-1 is not PN121. */
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9-]/g, '');
const NUMBER = /^(hr|s|hjres|sjres|hconres|sconres|hres|sres|pn)\d/;
const SHOWN = 20;                      // a section shows this many, then "Show all"

type Person = { key: string; id: string; name: string; sub: string };

function personSub(r: Row): string {
  const office = r.role === 'president' ? 'President' : r.role === 'vp' ? 'Vice President' : r.band === 'senate' ? 'Senate' : 'House';
  const post = r.leader && LEADER[r.leader.role] ? ' · ' + leaderShort(r.leader) : '';
  const yrs = `${r.years} ${r.years === 1 ? 'yr' : 'yrs'}`;
  return [office + post, r.party, r.role === 'president' || r.role === 'vp' ? null : r.seat, yrs,
    r.left && r.left_on ? 'left office ' + fmtLong(r.left_on) : null].filter(Boolean).join(' · ');
}

/** What Find finds, from the grid file already loaded: nothing is sent anywhere. People by a state or
 *  district, by the start of a surname, or by words in the name; items by number (the exact number
 *  first, then numbers that start with what was typed) and by words in the title. Words inside a name
 *  or a title count from three letters, so "IA" finds Iowa's members, not every name holding "ia". */
export function findIn(grid: GridDoc, q: string): { people: Person[]; items: Column[] } {
  const ql = q.trim().toLowerCase(), nq = norm(q), up = q.trim().toUpperCase();
  if (!ql) return { people: [], items: [] };
  const rank = (r: { full: string; last: string; seat: string | null }) => {
    const seat = (r.seat ?? '').toUpperCase();
    if (up.length >= 2 && (seat === up || seat.startsWith(up + '-'))) return 0;   // "IA", "CA-11"
    if (up.length >= 2 && seat.startsWith(up)) return 1;
    if (nq && norm(r.last).startsWith(nq)) return 2;
    if (ql.length > 2 && r.full.toLowerCase().includes(ql)) return 3;
    return -1;
  };
  const people: (Person & { rank: number })[] = [];
  grid.rows.forEach((r, i) => {
    const k = rank(r);
    if (k >= 0) people.push({ key: 'r' + i, id: r.id, name: r.full, sub: personSub(r), rank: k });
  });
  // the House's members for DC and the territories have cards but no rows
  for (const d of grid.delegates) {
    const k = rank({ full: d.full, last: d.full.split(' ').pop() ?? '', seat: d.seat });
    if (k >= 0) {
      people.push({ key: 'd' + d.id, id: d.id, name: d.full, rank: k,
        sub: `House · ${NON_STATES[d.seat.split('-')[0]] ?? d.seat} · cannot vote on final passage, so not in the grid` });
    }
  }
  people.sort((a, b) => a.rank - b.rank);
  const isNumber = NUMBER.test(nq);
  const tier = (c: Column) => {
    const n = norm(c.number);
    if (nq && n === nq) return 0;
    if (nq && n.startsWith(nq)) return 1;
    if (!isNumber && ql.length > 2 && ((c.title ?? '').toLowerCase().includes(ql) || foundAs(c, ql))) return 2;
    return -1;
  };
  const items = grid.columns.map((c) => ({ c, t: tier(c) })).filter((x) => x.t >= 0)
    .sort((a, b) => a.t - b.t).map((x) => x.c);
  return { people, items };
}

export function FindSheet({ grid, meta, onClose, openPerson }: {
  grid: GridDoc; meta: Meta | null; onClose: () => void;
  openPerson: (congress: number, id: string) => void;   // a person found in another term opens over that term
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayer(ref, onClose, 'input');
  const [q, setQ] = useState('');
  const [all, setAll] = useState({ people: false, items: false });
  useEffect(() => setAll({ people: false, items: false }), [q]);
  const { people, items } = useMemo(() => findIn(grid, q), [grid, q]);
  const typed = q.trim();
  const ql = typed.toLowerCase();
  // Nothing in this term: the other terms, read from their grid files only then (search.ts).
  const elsewhere = useElsewhere(meta, grid.congress, typed && !people.length && !items.length ? typed : '',
    (g) => findIn(g, typed), (x) => x.people.length + x.items.length > 0);
  const personRow = (p: Person, open: () => void) => (
    <button key={p.key} class="sr-find-row sr-find-person" onClick={open}>
      <span class="sr-find-text">
        <span class="sr-find-name">{p.name}</span>
        <span class="sr-find-sub">{p.sub}</span>
      </span>
      <span class="sr-find-go">Card ›</span>
    </button>
  );
  const itemRow = (c: Column) => {
    const day = outcomeDay(c);
    const as = foundAs(c, ql);
    return (
      <button key={c.id} class="sr-find-row sr-find-item" onClick={() => leaveSheet('/item/' + c.id)}>
        <span class="sr-find-text">
          <span class="sr-find-l1">
            <span class="sr-find-num">{c.number}</span>
            <span class={'sr-rule o-' + c.outcome} />
            <span class="sr-find-meta">{outcomeWords(c.outcome, c.kind)}{day ? ' · ' + fmtLong(day) : ''}</span>
          </span>
          <span class="sr-find-title">{c.title || c.number}</span>
          {as && <span class="sr-find-sub">Also titled “{as}”</span>}
        </span>
        <span class="sr-find-go">Open ›</span>
      </button>
    );
  };
  return (
    <div ref={ref} class="sr-layer">
      <div class="sr-find" role="dialog" aria-modal="true" aria-label="Find">
        <div class="sr-find-top">
          <input class="sr-find-input" type="text" inputMode="search" value={q} placeholder="Name, state, or bill" aria-label="Find: name, state, or bill"
                 autocomplete="off" autocapitalize="off" spellcheck={false} enterkeyhint="search"
                 onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          <button class="sr-sheet-b-close" onClick={onClose}>Cancel</button>
        </div>
        <div class="sr-find-results" aria-live="polite">
          {!typed && (
            <p class="sr-find-prompt">
              Type a surname, a state (IA), a district (CA-11) or a bill number (H.R. 5334). Nothing you type is stored.
            </p>
          )}
          {!!typed && !people.length && !items.length && (
            <div class="sr-find-none">
              <p>Nothing matches “{typed}”.</p>
              <p>Search by a member’s last name, a state, or a bill number such as H.R. 1. Nothing is hidden — this term simply has no member or item by that name.</p>
              {elsewhere === null && <p>Looking in the other terms…</p>}
            </div>
          )}
          {elsewhere?.map(({ term: t, found }) => (
            <section key={t.congress}>
              <h3 class="sr-find-label">In {t.label} · {found.people.length + found.items.length}</h3>
              {found.items.slice(0, SHOWN).map(itemRow)}
              {found.people.slice(0, SHOWN).map((p) => personRow(p, () => openPerson(t.congress, p.id)))}
            </section>
          ))}
          {!!people.length && (
            <section>
              <h3 class="sr-find-label">People · {people.length}</h3>
              {(all.people ? people : people.slice(0, SHOWN)).map((p) => personRow(p, () => leaveSheet('/member/' + p.id)))}
              {!all.people && people.length > SHOWN && (
                <button class="sr-find-more" onClick={() => setAll((s) => ({ ...s, people: true }))}>Show all {people.length} people</button>
              )}
            </section>
          )}
          {!!items.length && (
            <section>
              <h3 class="sr-find-label">Items · {items.length}</h3>
              {(all.items ? items : items.slice(0, SHOWN)).map(itemRow)}
              {!all.items && items.length > SHOWN && (
                <button class="sr-find-more" onClick={() => setAll((s) => ({ ...s, items: true }))}>Show all {items.length} items</button>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Term, items, key ------------------------------------------------------------------------

type BandTo = { key: string; label: string; count: string };
const SHORT_BAND: Record<string, string> = { pinned: 'Yours' };

export function OptionsSheet({ meta, term, filter, legend, bands, onTerm, onFilter, onJump, onClose, sortBy, onSort }: {
  meta: Meta; term: Term; filter: Filter; legend: Record<Mark, string> | null; bands: BandTo[];
  onTerm: (t: Term) => void; onFilter: (f: Filter) => void; onJump: (key: string) => void; onClose: () => void;
  sortBy: SortBy; onSort: (s: SortBy) => void;
}) {
  const RULES: [string, string][] = [
    ['became_law', 'Became law'], ['passed', 'Passed / agreed to'], ['failed_house', 'Failed'],
    ['vetoed', 'Vetoed'], ['law_without_signature', 'Law without signature'],
  ];
  return (
    <Sheet title="Term, items, key" action="Done" onClose={onClose}>
      <div class="sr-sheet-label" id="sr-opt-term">Congress</div>
      <div role="radiogroup" aria-labelledby="sr-opt-term">
        {meta.terms.map((t) => {
          const on = t.congress === term.congress;
          return (
            <button key={t.congress} class="sr-opt-term" role="radio" aria-checked={on} onClick={() => onTerm(t)}>
              <span class={'sr-opt-dot' + (on ? ' is-on' : '')} />
              <span class="sr-opt-term-text">
                <span class="sr-opt-term-name">{t.label} · {ordinal(t.congress)} Congress</span>
                <span class="sr-opt-term-sub">{peopleIn(t)} people · {t.columns} items</span>
              </span>
            </button>
          );
        })}
      </div>
      <div class="sr-opt-note">Earlier Congresses are not yet loaded.</div>
      <div class="sr-sheet-label" id="sr-opt-items">Items</div>
      <div class="sr-opt-seg" role="group" aria-labelledby="sr-opt-items">
        {FILTERS.map(([f, long, short]) => (
          <button key={f} class={'sr-opt-seg-btn' + (filter === f ? ' is-on' : '')} aria-pressed={filter === f}
                  aria-label={long} onClick={() => onFilter(f)}>{short}</button>
        ))}
      </div>
      <div class="sr-sheet-label" id="sr-opt-sort">Order people by</div>
      <div class="sr-opt-seg" role="group" aria-labelledby="sr-opt-sort">
        {([['tenure', 'Time in office'], ['name', 'Name']] as [SortBy, string][]).map(([s, label]) => (
          <button key={s} class={'sr-opt-seg-btn' + (sortBy === s ? ' is-on' : '')} aria-pressed={sortBy === s}
                  onClick={() => onSort(s)}>{label}</button>
        ))}
      </div>
      {/* the grid is every person, about 24,000px of them: a way to each band (Dan, 29 Sep 2026: "Yes, add it") */}
      {bands.length > 0 && (
        <>
          <div class="sr-sheet-label" id="sr-opt-jump">Jump to</div>
          <div class="sr-opt-seg" role="group" aria-labelledby="sr-opt-jump">
            {bands.map((b) => (
              <button key={b.key} class="sr-opt-seg-btn" aria-label={`${b.label}, ${b.count}`} onClick={() => onJump(b.key)}>
                {SHORT_BAND[b.key] ?? b.label}
              </button>
            ))}
          </div>
        </>
      )}
      {legend && (
        <>
          <div class="sr-sheet-label">Key</div>
          <div class="sr-opt-key">
            {KEY_ORDER.map((m) => (
              <div class="sr-opt-key-item" key={m}>
                <span class={'sr-cell sr-opt-key-cell c-' + cls(m)}>{GLYPH[m]}</span>
                <span class={KEY_DEF[m] ? 'has-def' : ''} data-def={KEY_DEF[m] || undefined}>{KEY_SHORT[m] || legend[m]}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <div class="sr-sheet-label">Item outcome</div>
      <div class="sr-opt-rules">
        {RULES.map(([code, label]) => (
          <div class="sr-opt-rule" key={code}><span class={'sr-rule o-' + code} /><span>{label}</span></div>
        ))}
      </div>
    </Sheet>
  );
}

// ---- Jump to a month -------------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Every month of the term, newest first, from the term's first January to its newest item, and how
 *  many of the items shown had their final vote in each. */
export function termMonths(grid: GridDoc, columns: number[]): { ym: string; n: number }[] {
  const count: Record<string, number> = {};
  for (const ci of columns) { const ym = grid.columns[ci].date.slice(0, 7); count[ym] = (count[ym] ?? 0) + 1; }
  const dates = grid.columns.map((c) => c.date).sort();
  if (!dates.length) return [];
  const first = [grid.label.slice(0, 4) + '-01', dates[0].slice(0, 7)].sort()[0];
  const out: { ym: string; n: number }[] = [];
  let [y, m] = dates[dates.length - 1].slice(0, 7).split('-').map(Number);
  for (;;) {
    const ym = `${y}-${String(m).padStart(2, '0')}`;
    if (ym < first) break;
    out.push({ ym, n: count[ym] ?? 0 });
    if (--m < 1) { m = 12; y--; }
  }
  return out;
}

/** The months to jump between: the grid's and an item's are the term's items by their final vote; a
 *  member's are that person's own, by the day of their chamber's or office's vote. */
export function MonthsSheet({ months, onJump, onClose }: {
  months: { ym: string; n: number }[]; onJump: (ym: string) => void; onClose: () => void;
}) {
  const max = Math.max(1, ...months.map((x) => x.n));
  return (
    <Sheet title="Jump to a month" onClose={onClose}>
      <p class="sr-months-note">
        Items with a final vote, by month. A way to move around, not the record: the marks are on the page you land on.
      </p>
      {months.map(({ ym, n }) => {
        const label = `${MONTHS[+ym.slice(5, 7) - 1]} ${ym.slice(0, 4)}`;
        return (
          <button key={ym} class="sr-month-row" disabled={!n} onClick={() => onJump(ym)}
                  aria-label={`${label}: ${n} ${n === 1 ? 'item' : 'items'}`}>
            <span class="sr-month-name">{label}</span>
            <span class="sr-month-track">{n > 0 && <span class="sr-month-bar" style={{ width: (n / max) * 100 + '%' }} />}</span>
            <span class="sr-month-n">{n} {n === 1 ? 'item' : 'items'}</span>
          </button>
        );
      })}
    </Sheet>
  );
}

// ---- Jump to a band --------------------------------------------------------------------------

/** From a band's label on the grid or an item page: Your representatives, White House, Senate, House. */
export function JumpSheet({ bands, onJump, onClose }: { bands: BandTo[]; onJump: (key: string) => void; onClose: () => void }) {
  return (
    <Sheet title="Jump to" onClose={onClose}>
      {bands.map((b) => (
        <button key={b.key} class="sr-menu-row sr-jump-row" onClick={() => onJump(b.key)}>
          <span>{b.label}</span><span class="sr-jump-n">{b.count}</span>
        </button>
      ))}
    </Sheet>
  );
}

// ---- Menu ------------------------------------------------------------------------------------

export const MENU: [string, string][] = [
  ['/', 'Home'], ['states', 'States'], ['/support', 'Support Us'], ['/how-this-works', 'How this works'],
  ['/corrections', 'Corrections log'], ['/who-pays', 'Who pays for this'], ['/privacy', 'Privacy'], ['/report', 'Report an error'],
];

export function MenuSheet({ current, onGo, onStates, onClose }: {
  current: string | null; onGo: (path: string) => void; onStates: () => void; onClose: () => void;
}) {
  const [share, setShare] = useState(false);
  // The panel opens below the fold of a sheet already full of pages: bring it into view.
  useEffect(() => {
    if (share) document.getElementById('sr-share-site-menu')?.scrollIntoView({ block: 'nearest' });
  }, [share]);
  return (
    <Sheet title="Votes on Record" onClose={onClose}>
      <nav aria-label="Pages">
        {MENU.map(([path, label]) => (
          <button key={path} class={'sr-menu-row' + (current === path ? ' is-current' : '')}
                  aria-current={current === path ? 'page' : undefined}
                  onClick={() => (path === 'states' ? onStates() : onGo(path))}>{label}</button>
        ))}
      </nav>
      {/* The desktop header's accounts and Share (Dan, 9 Oct 2026: "Yes" to them on the phone, in its Menu). */}
      <div class="sr-menu-social">
        <div class="sr-social"><SocialLinks /></div>
        <ShareButton open={share} onToggle={() => setShare(!share)} controls="sr-share-site-menu" />
        {share && <SharePanel id="sr-share-site-menu" what={siteShare(TAGLINE)} />}
      </div>
    </Sheet>
  );
}
